import React, { useEffect, useRef, useState } from 'react';
import { skills } from '../data/mock';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [activeCategoryName, setActiveCategoryName] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const skillCategories = Object.entries(skills);

  // Auto-cycle through skill cards
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % skillCategories.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isVisible, skillCategories.length]);

  // Auto-cycle through category names to make them bold
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveCategoryName((prev) => (prev + 1) % skillCategories.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [isVisible, skillCategories.length]);

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="py-14 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-gray-200/30 via-gray-100/20 to-transparent dark:from-gray-700/20 dark:via-gray-800/15 rounded-full blur-3xl opacity-40 animate-morph-reverse" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Section Title */}
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
        >
          Skills
        </h2>

        {/* Animated gradient underline - same as Experience */}
        <div className="relative h-1 mt-4 mb-16 overflow-hidden">
          <div
            className={`absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700 transition-all duration-1000 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
            style={{ transitionDelay: '200ms' }}
          />
          {/* Animated shimmer overlay */}
          <div
            className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDelay: '700ms' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skillCategories.map(([category, skillList], categoryIndex) => (
            <div
              key={category}
              className={`group relative bg-white dark:bg-gray-800 p-5 py-4 rounded-lg border border-gray-200 dark:border-gray-700
                transition-all duration-700 hover:shadow-2xl dark:hover:shadow-gray-900/50 hover:-translate-y-3
                overflow-hidden min-h-[180px] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              style={{ transitionDelay: `${categoryIndex * 100}ms` }}
            >
              {/* Animated rotating border - shows on hover or when active */}
              <div className={`absolute inset-0 transition-opacity duration-500 ${categoryIndex === activeCardIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                <div className="absolute inset-[-2px] animate-border-spin">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/60 to-transparent dark:via-gray-500/60 rounded-lg" />
                </div>
                <div className="absolute inset-[2px] bg-white dark:bg-gray-800 rounded-lg" />
              </div>

              {/* Content wrapper */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Skills as plain text - centered */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {skillList.map((skill, skillIndex) => (
                      <span
                        key={skill}
                        className={`text-sm font-light text-gray-700 dark:text-gray-300
                          hover:text-black dark:hover:text-white hover:font-medium
                          transition-all duration-300 cursor-default ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                          }`}
                        style={{ transitionDelay: `${categoryIndex * 100 + skillIndex * 30}ms` }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Category label at bottom with bold animation */}
                <div className="text-center mt-auto pt-2 border-t border-gray-200 dark:border-gray-800">
                  <h3 className={`text-xs uppercase tracking-wider transition-all duration-300 ${categoryIndex === activeCategoryName
                    ? 'font-bold text-black dark:text-white scale-105'
                    : 'font-medium text-gray-500 dark:text-gray-400'
                    }`}>
                    {category}
                  </h3>
                  {/* Mini animated underline */}
                  <div className="relative h-[2px] w-16 mx-auto mt-1 overflow-hidden bg-gray-200 dark:bg-gray-700 rounded-full">
                    <div className={`absolute inset-0 bg-gradient-to-r from-gray-400 via-gray-600 to-gray-400 dark:from-gray-500 dark:via-gray-300 dark:to-gray-500
                                    transition-opacity duration-300 ${categoryIndex === activeCategoryName ? 'opacity-100 animate-shimmer' : 'opacity-0 group-hover:opacity-100 group-hover:animate-shimmer'}`}
                      style={{ width: '50%' }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
